import clsx from 'clsx';
import React from 'react';
import { Link } from 'react-router';
import ChevronLeftIcon from '../../assets/icons/chevron-down-icon.svg?react';
import {
  GetCorrectiveActionAssignedUsingIdResponse,
  LoginResponseData,
} from '@EHSNavigator/backend';
import ReportIcon from '../../assets/icons/report-icon.svg?react';
import { useLocalStorage } from '@uidotdev/usehooks';

interface AssignedCorrectiveActionsDetailBannerProps {
  otherClasses?: string;
  data: GetCorrectiveActionAssignedUsingIdResponse['correctiveAction'];
}

const AssignedCorrectiveActionsDetailBanner: React.FC<
  AssignedCorrectiveActionsDetailBannerProps
> = ({ otherClasses, data }) => {
  const [userInfo] = useLocalStorage<LoginResponseData | null>(
    'userInfo',
    null,
  );

  const isEmployee = userInfo?.user?.role?.name === 'Employee';
  const isReporter = data?.incident?.reportedBy === userInfo?.user?.id;
  const incidentId = data?.incidentId;
  const incidentTitle = data?.incident?.title;
  const incidentRefId = data?.incident?.incidentRefId;

  const bannerClasses = clsx(
    otherClasses,
    'border-y-neutral-tones-text/10 my-6 border-y py-5 lg:my-9',
  );

  const renderBannerContent = () => (
    <div className="bg-card-backgrounds/5 border-neutral-tones-text/10 rounded-10 flex w-full items-center gap-2.5 border p-15">
      <ReportIcon className="[&>path]:fill-brand-fair-orange h-12 w-12 md:h-9 md:w-9 lg:h-12 lg:w-12" />
      <div className="flex flex-1 flex-col gap-1 lg:gap-2.5">
        <span className="font-inter-variable text-brand-fair-orange -tracking-form-input lg:-tracking-button text-base leading-5 font-medium lg:text-xl lg:leading-6 lg:font-bold">
          Linked Incident
        </span>
        <div className="flex items-center gap-15">
          <p className="text-neutral-tones-text -tracking-form-input lg:-tracking-text-area line-clamp-1 text-base leading-5 font-bold capitalize lg:text-lg lg:leading-5">
            {incidentTitle}
          </p>
          <span className="border-l-neutral-tones-text/10 hidden h-5 border-l lg:block" />
          <p className="font-inter-variable text-neutral-tones-text/80 -tracking-form-label lg:-tracking-form-input hidden text-sm leading-5 font-normal whitespace-nowrap lg:block lg:text-base">
            Reference ID{' '}
            <span className="text-neutral-tones-text/50 uppercase">
              {incidentRefId}
            </span>
          </p>
        </div>
      </div>
      <Link
        target="_blank"
        to={
          isEmployee
            ? `/my-reported-incidents/${incidentId}`
            : `/incident-review/${incidentId}`
        }
      >
        <ChevronLeftIcon className="[&>path]:fill-neutral-tones-text h-6 w-6 -rotate-90 md:h-12 md:w-12" />
      </Link>
    </div>
  );

  if (isEmployee && !isReporter) {
    return null;
  }

  return (
    <div
      className={bannerClasses}
      data-testid="assigned-corrective-actions-detail-banner"
    >
      {renderBannerContent()}
    </div>
  );
};

export default AssignedCorrectiveActionsDetailBanner;
